// @flow
import { useContext, useState } from "react";
import { css, ThemeContext } from "styled-components";
import { useHistory } from "react-router";
import { Sentry } from "@nested/isomorphic-sentry";
import { Button } from "@nested/component-library";
import { subscribeToNewsletterZap } from "@nested/utils";
import { media, TextInput } from "@nested/brand";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope } from "@fortawesome/free-solid-svg-icons";

const inputIcon = css`
  position: absolute;
  top: 20px;
  left: 20px;
  z-index: 10;
  font-size: 20px;
`;

const inputWrapperStyle = css`
  position: relative;
  margin: 30px auto 0;
  max-width: 350px;

  ${media.tablet`
    margin: 30px 0;
  `}
`;

const inputIllusionBox = css`
  background-color: white;
  border: 1px solid ${({ theme }) => theme.palette.hague40};
  border-top: none;
  border-radius: 0 0 4px 4px;
  padding: 0 10px 10px;
  button {
    width: 100%;
    height: 50px;
    color: ${({ theme }) => theme.palette.hague130};
  }
  button:hover,
  button:disabled:hover {
    background-color: ${({ theme }) => theme.palette.terracotta90};
    color: ${({ theme }) => theme.palette.hague130};
  }
  button:active,
  button:disabled:active {
    background-color: #ff6d56;
    color: ${({ theme }) => theme.palette.hague130};
  }
  button:disabled {
    background-color: ${({ theme }) => theme.palette.terracotta100};
    color: ${({ theme }) => theme.palette.hague130};
  }
`;

const errorMessageStyle = css`
  margin: 0;
  padding: 0 0 10px 0;
  color: #fe6a50;
  font-weight: 500;
  text-align: left;
`;

type Props = {
  className?: string,
  submitButtonText: string,
};

export const EmailCaptureForm = ({ className, submitButtonText }: Props) => {
  const history = useHistory();
  const theme = useContext(ThemeContext);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState();

  const onSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);

    const error = await subscribeToNewsletterZap(email);

    if (error) {
      setErrorMessage(error);
      setSubmitting(false);
      return;
    }

    try {
      // email is set in localstorage so it can be skipped from the onboarding form
      window.localStorage.setItem("email", email);
    } catch (e) {
      Sentry.captureException(e);
    } finally {
      // localstorage fails - progress anyway
      setSubmitting(false);
      history.push("/get-started");
    }
  };

  return (
    <form onSubmit={onSubmit} className={className} css={inputWrapperStyle}>
      <FontAwesomeIcon
        color={theme.palette.terracotta100}
        icon={faEnvelope}
        css={inputIcon}
      />
      <TextInput
        css={`
          input {
            box-shadow: none;
          }
        `}
        name="email"
        className="fs-exclude"
        withIcon
        hasButtonBelow
        placeholder="Enter your email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
      <div css={inputIllusionBox} className="illusion-box">
        {errorMessage && <p css={errorMessageStyle}>{errorMessage}</p>}
        <Button
          type="accent"
          data-test="stacked-address-picker-submit"
          disabled={submitting}
        >
          {submitButtonText}
        </Button>
      </div>
    </form>
  );
};
